import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Save,
} from '@mui/icons-material';
import {
  useTeam,
  useCreateTeam,
  useUpdateTeam,
  useSports,
  useOrganizations,
} from '@/hooks/useApi';
import { LoadingState } from '@/components';
import type { SportType } from '@/types';
import type { CitySuggestionOption } from '@/utils/dadata';
import { fetchCitySuggestions } from '@/utils/dadata';
import { ensureCity, getCityById } from '@/utils/cityRegistry';
import { isUUID } from '@/utils/uuid';
import { getApiErrorMessage } from '@/utils/apiError';

export const CreateTeamPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo');
  const presetSport = params.get('sportType');
  const isEdit = Boolean(id);
  const [formData, setFormData] = useState({
    name: '',
    cityId: '',
    cityLabel: '',
    sportType: (presetSport || '') as SportType,
    organizationId: '',
    active: true,
  });
  const [cityInputValue, setCityInputValue] = useState('');
  const [cityOptions, setCityOptions] = useState<CitySuggestionOption[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const dadataToken = import.meta.env.VITE_DADATA_TOKEN?.trim() ?? '';

  const { data: sports, isLoading: sportsLoading } = useSports();
  const { data: organizations } = useOrganizations();
  const { data: team, isLoading: teamLoading } = useTeam(id || '', { enabled: isEdit });
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();

  useEffect(() => {
    if (!isEdit || !team) return;
    const city = getCityById(team.cityId || undefined);
    setFormData({
      name: team.name,
      cityId: team.cityId || '',
      cityLabel: city?.name || '',
      sportType: team.sportType,
      organizationId: team.organizationId || '',
      active: team.active,
    });
  }, [isEdit, team]);

  useEffect(() => {
    setCityInputValue(formData.cityLabel);
  }, [formData.cityLabel]);

  useEffect(() => {
    const query = cityInputValue.trim();
    if (!dadataToken || query.length < 3) {
      setCityOptions([]);
      setCityLoading(false);
      return;
    }
    let isActive = true;
    const timeoutId = window.setTimeout(async () => {
      setCityLoading(true);
      try {
        const options = await fetchCitySuggestions(query, dadataToken);
        if (isActive) setCityOptions(options);
      } catch {
        if (isActive) setCityOptions([]);
      } finally {
        if (isActive) setCityLoading(false);
      }
    }, 400);
    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [cityInputValue, dadataToken]);

  const handleSubmit = async () => {
    setSubmitError('');
    try {
      if (isEdit && id) {
        await updateTeam.mutateAsync({
          id,
          data: {
            name: formData.name,
            cityId: isUUID(formData.cityId) ? formData.cityId : null,
            active: formData.active,
          },
        });
        navigate(`/teams/${id}`);
        return;
      }

      const created = await createTeam.mutateAsync({
        ...formData,
        cityId: isUUID(formData.cityId) ? formData.cityId : null,
      });
      const next = returnTo
        ? `/teams/${created.id}?returnTo=${encodeURIComponent(returnTo)}`
        : `/teams/${created.id}`;
      navigate(next);
    } catch (error) {
      setSubmitError(getApiErrorMessage(
        error,
        isEdit ? 'Не удалось сохранить команду.' : 'Не удалось создать команду.',
      ));
    }
  };

  if (sportsLoading || (isEdit && teamLoading)) return <LoadingState message="Загрузка..." />;

  return (
    <Box sx={{ pb: 10 }}>
      <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(isEdit && id ? `/teams/${id}` : (returnTo || '/teams'))}>Назад</Button>
        <Typography variant="h6" fontWeight="bold">{isEdit ? 'Редактировать команду' : 'Новая команда'}</Typography>
      </Paper>

      <Paper sx={{ p: 3, mx: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <TextField
            label="Название команды"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <Autocomplete
            freeSolo
            options={cityOptions.map((option) => option.label)}
            value={formData.cityLabel}
            inputValue={cityInputValue}
            onInputChange={(_, value) => {
              setCityInputValue(value);
              setFormData({ ...formData, cityLabel: value });
            }}
            onChange={(_, value) => {
              const normalizedValue = typeof value === 'string' ? value : '';
              setCityInputValue(normalizedValue);
              const selected = cityOptions.find((option) => option.label === normalizedValue);
              const city = selected ? ensureCity(selected) : undefined;
              setFormData({
                ...formData,
                cityId: city?.id || '',
                cityLabel: normalizedValue,
              });
            }}
            noOptionsText={
              cityInputValue.trim().length < 3
                ? 'Введите минимум 3 символа'
                : 'Города не найдены'
            }
            loading={cityLoading}
            disabled={!dadataToken}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Город"
                placeholder={!dadataToken ? 'Добавьте VITE_DADATA_TOKEN в ENV' : 'Начните вводить город'}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {cityLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />

          <FormControl fullWidth required>
            <InputLabel>Вид спорта</InputLabel>
            <Select
              value={formData.sportType}
              onChange={(e) => setFormData({ ...formData, sportType: e.target.value as SportType })}
              disabled={isEdit}
            >
              {sports?.map((sport) => (
                <MenuItem key={sport.id} value={sport.code}>{sport.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Организация</InputLabel>
            <Select
              value={formData.organizationId}
              onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
              disabled={isEdit}
            >
              <MenuItem value="">Без организации</MenuItem>
              {organizations?.data.map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Активна"
          />

          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={
              !formData.name ||
              (!isEdit && !formData.sportType) ||
              createTeam.isPending ||
              updateTeam.isPending
            }
            size="large"
          >
            {createTeam.isPending || updateTeam.isPending
              ? (isEdit ? 'Сохранение...' : 'Создание...')
              : (isEdit ? 'Сохранить изменения' : 'Создать команду')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
